import { useEffect, useRef } from 'react';
import { useLocale } from '../../application/i18n/LocaleProvider';
import {
    SWITCH_GROUP_KEY,
    countActiveFilters,
    isFilterActive,
    listFilterGroups,
} from '../../domain/plants/plantFilters';
import { BAND_ICONS, TRAIT_ICONS } from './PlantIcons';
import WaterMeter from './WaterMeter';

/**
 * The filter sheet (SPEC-011 D-5, recomposed by SPEC-012 D-4).
 *
 * A real `<dialog>` opened with `showModal()`, for the reason SPEC-006 D-3 gives:
 * focus trapping, Escape, the top layer and an inert background are four things
 * the platform gets right and a hand-rolled bottom sheet gets subtly wrong.
 *
 * Filtering is live. The primary button says how many plants are waiting behind
 * it, which is only true if the count moves as boxes are ticked — so the button
 * dismisses the sheet and does not commit anything. There is nothing to cancel,
 * which is why there is no Cancel.
 *
 * Every control in here is a real checkbox. The sheet has always been
 * multi-select — within a group the options are OR-ed — but SPEC-011 drew them as
 * a row of pills that read as a segmented control, where one choice looks like it
 * replaces the last. Tiles with a checked state, and switches for the strengths,
 * say what the model has always done.
 *
 * The groups are whatever the catalogue in hand supports: an option no plant
 * satisfies is not offered, and neither is a group with one option
 * (`listFilterGroups`).
 */

/** The glyph for one option, from the same table the card draws its row from. */
function optionIcon(groupKey, value) {
    return groupKey === SWITCH_GROUP_KEY ? TRAIT_ICONS[value] : BAND_ICONS[groupKey]?.[value];
}

/**
 * What a tile shows above its label.
 *
 * Water is the meter, at the band's own level, because that is what the card
 * draws: a tile with one droplet filled is a picture of the cards it keeps. Every
 * other group changes shape instead, and looks its glyph up.
 */
function OptionGlyph({ groupKey, option }) {
    if (groupKey === 'water') {
        return <WaterMeter score={option.score} size={22} />;
    }

    const Glyph = optionIcon(groupKey, option.value);

    return Glyph ? <Glyph size={24} /> : null;
}

/**
 * One band, as a tile: glyph, the word the card prints, and the boundary under it.
 *
 * The `<input>` is a real checkbox clipped to a pixel — the whole tile is the
 * 44px target, and the checked state is drawn from `:checked` in CSS so the
 * keyboard, the screen reader and the pointer are all operating one control.
 */
function OptionTile({ groupKey, option, checked, onToggle }) {
    const { t } = useLocale();

    return (
        <label className={`filter-option filter-option--${groupKey}${checked ? ' filter-option--on' : ''}`}>
            <input
                type="checkbox"
                className="filter-option__input"
                checked={checked}
                onChange={onToggle}
            />
            <OptionGlyph groupKey={groupKey} option={option} />
            <span className="filter-option__label">{t(option.labelKey)}</span>
            <span className="filter-option__caption">{t(option.captionKey)}</span>
        </label>
    );
}

/**
 * One strength, as a switch.
 *
 * `role="switch"` on a checkbox, because that is what it is: an on/off with no
 * third state, and the platform's checkbox is what makes it operable by keyboard
 * without a line of JavaScript. The caption is what the trait actually means —
 * "Attracts bees, butterflies and other pollinators" beside "Pollinator friendly"
 * — and is tied to the control with `aria-describedby` rather than left as text
 * that happens to sit nearby.
 */
function SwitchRow({ option, checked, onToggle }) {
    const { t } = useLocale();
    const OptionIcon = TRAIT_ICONS[option.value];
    const captionId = `filter-switch-${option.value.replace(/\W/gu, '-')}`;

    return (
        <label className="filter-switch">
            {OptionIcon ? <OptionIcon size={22} /> : null}
            <span className="filter-switch__text">
                <span className="filter-switch__label">{t(option.labelKey)}</span>
                <span className="filter-switch__caption" id={captionId}>{t(option.captionKey)}</span>
            </span>
            <input
                type="checkbox"
                role="switch"
                className="filter-switch__input"
                checked={checked}
                onChange={onToggle}
                aria-describedby={captionId}
            />
            <span className="filter-switch__track" aria-hidden="true" />
        </label>
    );
}

export default function FilterSheet({ plants, filters, onToggleFilter, onClear, onClose, resultCount }) {
    const { t } = useLocale();
    const dialogRef = useRef(null);
    const groups = listFilterGroups(plants);
    const activeCount = countActiveFilters(filters);

    // Opening is a one-off, and deliberately not in the same effect as the
    // listener below: ticking a box re-renders this component, and an effect
    // that depended on the handler would close and re-open the dialog on every
    // keystroke — losing focus, and throwing on the second `showModal()`.
    useEffect(() => {
        const dialog = dialogRef.current;

        dialog.showModal();

        return () => {
            if (dialog.open) {
                dialog.close();
            }
        };
    }, []);

    // One exit for every way out: the apply button calls `close()`, Escape and a
    // tap on the backdrop make the browser call it, and this fires either way.
    useEffect(() => {
        const dialog = dialogRef.current;

        dialog.addEventListener('close', onClose);

        return () => dialog.removeEventListener('close', onClose);
    }, [onClose]);

    const applyKey = resultCount === 0
        ? 'plants.filter.applyNone'
        : (resultCount === 1 ? 'plants.filter.applyOne' : 'plants.filter.apply');

    return (
        <dialog
            className="filter-sheet"
            ref={dialogRef}
            aria-labelledby="filter-sheet-title"
            // A click that lands on the dialog element itself landed on the
            // backdrop: everything inside is a child. The one place a phone's
            // "tap outside to dismiss" has to be wired by hand.
            onClick={(event) => {
                if (event.target === dialogRef.current) {
                    dialogRef.current.close();
                }
            }}
        >
            {/* The grab handle the approved sheet draws. Decorative: this sheet is
                dismissed by the button, by Escape or by a tap outside, and a bar
                that only looks draggable must not claim a role. */}
            <span className="filter-sheet__grip" aria-hidden="true" />

            <div className="filter-sheet__head">
                <h2 className="filter-sheet__title" id="filter-sheet-title">{t('plants.filter.title')}</h2>
                {/* Reset belongs beside the title rather than in the footer, so the
                    footer carries exactly one action (NFR-05). Hidden rather than
                    disabled when nothing is on: a permanently greyed control in the
                    header is furniture. */}
                {activeCount > 0 ? (
                    <button type="button" className="filter-sheet__clear" onClick={onClear}>
                        {t('plants.filter.clearAll')}
                    </button>
                ) : null}
            </div>

            <div className="filter-sheet__body">
                {groups.map((group) => (group.key === SWITCH_GROUP_KEY ? (
                    <fieldset className="filter-group filter-group--switches" key={group.key}>
                        <legend className="filter-group__legend">{t(group.labelKey)}</legend>
                        {group.options.map((option) => (
                            <SwitchRow
                                key={option.value}
                                option={option}
                                checked={isFilterActive(filters, group.key, option.value)}
                                onToggle={() => onToggleFilter(group.key, option.value)}
                            />
                        ))}
                    </fieldset>
                ) : (
                    <fieldset className="filter-group" key={group.key}>
                        <legend className="filter-group__legend">{t(group.labelKey)}</legend>
                        {/* Three or four tiles across, from the group's own length
                            — `auto-fit` would lay three options out in four columns
                            and leave a hole where a fourth tile is not. */}
                        <div
                            className="filter-group__options"
                            style={{ '--filter-columns': Math.min(group.options.length, 4) }}
                        >
                            {group.options.map((option) => (
                                <OptionTile
                                    key={option.value}
                                    groupKey={group.key}
                                    option={option}
                                    checked={isFilterActive(filters, group.key, option.value)}
                                    onToggle={() => onToggleFilter(group.key, option.value)}
                                />
                            ))}
                        </div>
                    </fieldset>
                )))}
            </div>

            <div className="filter-sheet__foot">
                <button
                    type="button"
                    className="button button--primary filter-sheet__apply"
                    onClick={() => dialogRef.current.close()}
                >
                    {t(applyKey, { count: resultCount })}
                </button>
            </div>
        </dialog>
    );
}
